(() => {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const header = document.querySelector('[data-sa-menu-toggle]');
  const menu = document.querySelector('[data-sa-menu]');
  if (header && menu) {
    const desktopNav = window.matchMedia('(min-width: 1101px)');
    const items = [...menu.querySelectorAll('.sa-menu__item:not(.sa-menu__item--link)')];
    let hideTimer;

    const setOpen = (item, open) => {
      item.classList.toggle('is-open', open);
      item.querySelector('.sa-menu__trigger')?.setAttribute('aria-expanded', String(open));
    };

    const closeAll = () => items.forEach((item) => setOpen(item, false));

    const openItem = (item) => {
      clearTimeout(hideTimer);
      items.forEach((other) => {
        const on = other === item;
        setOpen(other, on);
        if (!on) other.querySelector('.sa-menu__trigger')?.blur();
      });
    };

    const scheduleClose = () => {
      clearTimeout(hideTimer);
      hideTimer = setTimeout(closeAll, 160);
    };

    header.addEventListener('click', () => {
      const open = menu.classList.toggle('is-open');
      header.setAttribute('aria-expanded', String(open));
    });

    items.forEach((item) => {
      const trigger = item.querySelector('.sa-menu__trigger');
      trigger?.addEventListener('click', (event) => {
        if (desktopNav.matches) {
          event.preventDefault();
          openItem(item);
          return;
        }
        const next = !item.classList.contains('is-open');
        items.forEach((other) => setOpen(other, other === item && next));
      });
      item.addEventListener('mouseenter', () => {
        if (desktopNav.matches) openItem(item);
      });
      item.addEventListener('mouseleave', () => {
        if (desktopNav.matches) scheduleClose();
      });
      trigger?.addEventListener('focus', () => {
        if (desktopNav.matches) openItem(item);
      });
    });

    menu.addEventListener('mouseleave', () => {
      if (desktopNav.matches) scheduleClose();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeAll();
        document.activeElement instanceof HTMLElement && document.activeElement.blur();
      }
    });

    // Close mega-menu when any link inside a panel is clicked.
    // Needed for same-page anchor links (e.g. #bulk-orders) that don't
    // trigger a page navigation, so the menu would otherwise stay open.
    menu.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (link) {
        clearTimeout(hideTimer);
        closeAll();
        menu.classList.remove('is-open');
        header.setAttribute('aria-expanded', 'false');
      }
    });
  }

  const userMenu = document.querySelector('[data-sa-user]');
  const userToggle = userMenu?.querySelector('[data-sa-user-toggle]');
  const userPanel = userMenu?.querySelector('[data-sa-user-menu]');

  if (userMenu && userToggle && userPanel) {
    let userHideTimer;

    const closeUser = (focusToggle = false) => {
      if (!userMenu.classList.contains('is-open')) return;
      userMenu.classList.remove('is-open');
      userToggle.setAttribute('aria-expanded', 'false');
      clearTimeout(userHideTimer);
      userHideTimer = setTimeout(() => {
        userPanel.hidden = true;
      }, 180);
      if (focusToggle) userToggle.focus();
    };

    const openUser = () => {
      clearTimeout(userHideTimer);
      userPanel.hidden = false;
      userToggle.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => userMenu.classList.add('is-open'));
    };

    userToggle.addEventListener('click', (event) => {
      event.stopPropagation();
      if (userMenu.classList.contains('is-open')) closeUser();
      else openUser();
    });

    document.addEventListener('click', (event) => {
      if (!userMenu.contains(event.target)) closeUser();
    });

    userMenu.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeUser(true);
    });

    userMenu.addEventListener('focusout', (event) => {
      if (event.relatedTarget && !userMenu.contains(event.relatedTarget)) closeUser();
    });
  }

  const searchOpen = document.querySelector('[data-sa-search-open]');
  const searchPanel = document.querySelector('[data-sa-search-panel]');
  const searchForm = document.querySelector('[data-sa-search-form]');
  const searchInput = document.querySelector('[data-sa-search-input]');
  const searchSuggest = document.querySelector('[data-sa-search-suggest]');
  const searchClear = document.querySelector('[data-sa-search-clear]');

  if (searchOpen && searchPanel && searchInput && searchSuggest && searchForm) {
    let activeIndex = -1;
    let fetchTimer;
    let fetchController;
    let fetchSequence = 0;
    const suggestBase =
      (window.Theme && Theme.routes && Theme.routes.predictive_search_url) ||
      '/search/suggest';
    const searchPageUrl = (window.Theme && Theme.routes && Theme.routes.search_url) || '/search';

    const getItems = () => [...searchSuggest.querySelectorAll('[data-sa-search-item]')];

    const setActiveItem = (index) => {
      const items = getItems();
      activeIndex = index;
      items.forEach((item, i) => item.classList.toggle('is-active', i === activeIndex));
      if (items[activeIndex]) {
        searchInput.setAttribute('aria-activedescendant', items[activeIndex].id);
      } else {
        searchInput.removeAttribute('aria-activedescendant');
      }
    };

    const hideSuggest = () => {
      searchSuggest.hidden = true;
      searchInput.setAttribute('aria-expanded', 'false');
      setActiveItem(-1);
    };

    const showSuggest = () => {
      searchSuggest.hidden = false;
      searchInput.setAttribute('aria-expanded', 'true');
    };

    const escapeHtml = (value) =>
      String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const renderSuggest = async (products, query) => {
      const safeQuery = escapeHtml(query);
      if (!products.length) {
        searchSuggest.innerHTML = `<p class="sa-search-panel__status">No products found for “${safeQuery}”. Try another search.</p>`;
        showSuggest();
        return;
      }

      const usd = window.SeatArmourUsdPrices;
      const items = (
        await Promise.all(
          products.map(async (product, index) => {
            const image = product.featured_image?.url || product.image || '';
            const handle = product.handle || '';
            const price =
              handle && usd ? await usd.fetchShopPriceLabel(handle) : '';
            const itemId = `sa-search-item-${index}`;
            return `
            <li>
              <a
                class="sa-search-panel__item"
                id="${itemId}"
                href="${product.url}"
                data-sa-search-item
                role="option"
              >
                ${
                  image
                    ? `<img class="sa-search-panel__thumb" src="${image}" alt="" width="58" height="58" loading="lazy">`
                    : '<span class="sa-search-panel__thumb" aria-hidden="true"></span>'
                }
                <span class="sa-search-panel__meta">
                  <strong>${escapeHtml(product.title)}</strong>
                  ${price ? `<span>${price}</span>` : ''}
                </span>
              </a>
            </li>`;
          })
        )
      ).join('');

      const viewAllUrl = `${searchPageUrl}?type=product&q=${encodeURIComponent(query)}`;
      searchSuggest.innerHTML = `
        <ul class="sa-search-panel__results">${items}</ul>
        <div class="sa-search-panel__footer">
          <a href="${viewAllUrl}">View all results for “${safeQuery}”</a>
        </div>`;
      showSuggest();
      setActiveItem(-1);
    };

    const normalizeSuggestTags = (tags) => {
      if (Array.isArray(tags)) return tags.map((entry) => String(entry || '').trim()).filter(Boolean);
      if (typeof tags === 'string') {
        return tags
          .split(',')
          .map((entry) => entry.trim())
          .filter(Boolean);
      }
      return [];
    };

    const suggestProductPath = (url) => {
      const raw = String(url || '').trim();
      if (!raw) return '';
      try {
        return new URL(raw, window.location.origin).pathname;
      } catch (_) {
        return raw.split('?')[0].split('#')[0];
      }
    };

    const productMatchesQuery = (product, query) => {
      const tokens = String(query || '')
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter((token) => token.length >= 2);
      if (!tokens.length) return true;
      const haystack = [
        product.title,
        product.product_type || product.type,
        product.vendor,
        product.handle,
        suggestProductPath(product.url),
        ...normalizeSuggestTags(product.tags),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return tokens.every((token) => haystack.includes(token));
    };

    const fetchSuggestions = async (query) => {
      const sequence = ++fetchSequence;
      if (fetchController) fetchController.abort();
      fetchController = new AbortController();
      const requestController = fetchController;
      const timeoutId = window.setTimeout(() => requestController.abort(), 8000);

      const suggestUrl = suggestBase.endsWith('.json') ? suggestBase : `${suggestBase}.json`;
      const url = new URL(suggestUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');

      searchSuggest.innerHTML = '<p class="sa-search-panel__status">Searching products…</p>';
      showSuggest();

      try {
        const response = await fetch(url.toString(), {
          signal: requestController.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('Search request failed');
        const data = await response.json();
        if (sequence !== fetchSequence) return;
        const products = (data?.resources?.results?.products || []).filter((product) =>
          productMatchesQuery(product, query)
        );
        renderSuggest(products.slice(0, 8), query);
      } catch (error) {
        if (error.name === 'AbortError' || sequence !== fetchSequence) return;
        searchSuggest.innerHTML = '<p class="sa-search-panel__status">Could not load suggestions. Press Search to try again.</p>';
        showSuggest();
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    const closeSearch = () => {
      searchPanel.classList.remove('is-open');
      searchOpen.classList.remove('is-active');
      searchOpen.setAttribute('aria-expanded', 'false');
      window.setTimeout(() => {
        searchPanel.hidden = true;
      }, 320);
      hideSuggest();
      if (fetchController) fetchController.abort();
    };

    const openSearch = () => {
      const navMenu = document.querySelector('[data-sa-menu]');
      navMenu?.classList.remove('is-open');
      navMenu?.querySelectorAll('.sa-menu__item.is-open').forEach((item) => {
        item.classList.remove('is-open');
        item.querySelector('.sa-menu__trigger')?.setAttribute('aria-expanded', 'false');
      });
      document.querySelector('[data-sa-menu-toggle]')?.setAttribute('aria-expanded', 'false');

      searchPanel.hidden = false;
      searchOpen.classList.add('is-active');
      searchOpen.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        searchPanel.classList.add('is-open');
        searchInput.focus();
        searchInput.select();
      });
    };

    const toggleSearch = () => {
      if (searchPanel.classList.contains('is-open')) {
        closeSearch();
      } else {
        openSearch();
      }
    };

    searchOpen.addEventListener('click', toggleSearch);
    searchPanel.querySelectorAll('[data-sa-search-close]').forEach((control) => {
      control.addEventListener('click', closeSearch);
    });

    searchClear?.addEventListener('click', () => {
      searchInput.value = '';
      searchClear.hidden = true;
      hideSuggest();
      searchInput.focus();
    });

    searchInput.addEventListener('input', () => {
      const query = searchInput.value.trim();
      searchClear.hidden = !query.length;
      clearTimeout(fetchTimer);
      if (!query.length) {
        hideSuggest();
        if (fetchController) fetchController.abort();
        return;
      }
      fetchTimer = window.setTimeout(() => fetchSuggestions(query), 220);
    });

    searchInput.addEventListener('keydown', (event) => {
      const items = getItems();
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!items.length) return;
        setActiveItem((activeIndex + 1) % items.length);
        items[activeIndex]?.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (!items.length) return;
        setActiveItem((activeIndex - 1 + items.length) % items.length);
        items[activeIndex]?.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && activeIndex >= 0 && items[activeIndex]) {
        event.preventDefault();
        window.location.href = items[activeIndex].href;
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeSearch();
        searchOpen.focus();
      }
    });

    document.addEventListener('click', (event) => {
      if (!searchPanel.classList.contains('is-open')) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (searchPanel.contains(target) || searchOpen.contains(target)) return;
      closeSearch();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && searchPanel.classList.contains('is-open')) {
        closeSearch();
        searchOpen.focus();
      }
    });
  }

  const initHeroVideos = (hero) => {
    const dataEl = hero.querySelector('[data-sa-hero-playlist]');
    const video = hero.querySelector('[data-sa-hero-video]');
    if (!dataEl || !video) return;

    let playlist = [];
    try {
      playlist = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }
    playlist = playlist.filter((item) => item && item.name && item.src);
    if (!playlist.length) return;

    const COOKIE = 'sa_hero_played';
    const readPlayed = () => {
      const match = document.cookie.match(/(?:^|; )sa_hero_played=([^;]*)/);
      if (!match) return [];
      try {
        return decodeURIComponent(match[1]).split('|').filter(Boolean);
      } catch (error) {
        return [];
      }
    };
    const writePlayed = (names) => {
      const value = encodeURIComponent(names.join('|'));
      document.cookie = `${COOKIE}=${value}; path=/; max-age=2592000; SameSite=Lax`;
    };
    const clearPlayed = () => {
      document.cookie = `${COOKIE}=; path=/; max-age=0; SameSite=Lax`;
    };

    const known = new Set(playlist.map((item) => item.name));
    let played = readPlayed().filter((name, i, list) => known.has(name) && list.indexOf(name) === i);
    if (playlist.every((item) => played.includes(item.name))) {
      clearPlayed();
      played = [];
    }

    const nextUnplayed = (from) => {
      for (let step = 0; step < playlist.length; step += 1) {
        const i = (from + step) % playlist.length;
        if (!played.includes(playlist[i].name)) return i;
      }
      return 0;
    };

    let index = nextUnplayed(0);
    let errorStreak = 0;
    let audioFix = 0;
    let soundUnlocked = false;
    const stage = video.closest('[data-sa-hero-stage]');
    const defaultVolume = 0.25;
    let volume = defaultVolume;
    let userMuted = false;
    let syncingAudio = false;

    const applyAudio = () => {
      syncingAudio = true;
      video.muted = userMuted;
      video.volume = volume;
      if (!userMuted) {
        video.muted = false;
        video.volume = volume;
      }
      syncingAudio = false;
    };

    const showCopy = (item) => {
      const title = hero.querySelector('.sa-hero__slide.is-active .sa-hero__title');
      const sub = hero.querySelector('.sa-hero__slide.is-active .sa-hero__sub');
      if (title && item?.title) {
        title.replaceChildren();
        String(item.title).split('|').forEach((line, lineIndex) => {
          if (lineIndex) title.appendChild(document.createElement('br'));
          title.appendChild(document.createTextNode(line));
        });
      }
      if (sub && item?.subtitle) sub.textContent = item.subtitle;
    };

    const fitHeight = () => {
      if (!stage) return;
      const slides = stage.parentElement;
      if (!slides) return;
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      if (mobile) {
        stage.style.width = '100%';
        stage.style.height = 'auto';
        stage.style.maxWidth = '100%';
        slides.style.setProperty('--sa-hero-video-w', '100%');
        return;
      }
      stage.style.height = '';
      stage.style.maxWidth = '';
      if (!video.videoWidth || !video.videoHeight) return;
      const slidesH = slides.clientHeight;
      const slidesW = slides.clientWidth;
      if (!slidesH || !slidesW) return;
      const natural = slidesH * (video.videoWidth / video.videoHeight);
      const blend = Math.min(180, Math.max(72, slidesW * 0.16));
      let width = Math.min(natural, slidesW);
      const slidesBox = slides.getBoundingClientRect();
      const content = hero.querySelector('.sa-hero__slide.is-active .sa-hero__content');
      if (content) {
        const contentBox = content.getBoundingClientRect();
        const coversHeight = contentBox.bottom >= slidesBox.bottom - 8;
        if (coversHeight) {
          const panel = Math.max(0, contentBox.right - slidesBox.left);
          const room = Math.max(slidesW * 0.34, slidesW - panel + blend);
          width = Math.min(width + blend, room, slidesW);
        }
      }
      const fitted = Math.round(width);
      stage.style.width = `${fitted}px`;
      slides.style.setProperty('--sa-hero-video-w', `${fitted}px`);
    };

    const startPlayback = () => {
      applyAudio();
      const pending = video.play();
      if (!pending || typeof pending.catch !== 'function') return;
      pending.catch(() => {
        if (soundUnlocked && !userMuted) {
          const retry = () => {
            applyAudio();
            video.play().catch(() => {});
          };
          if (video.readyState >= 2) retry();
          else video.addEventListener('canplay', retry, { once: true });
          return;
        }
        syncingAudio = true;
        video.muted = true;
        video.volume = volume;
        syncingAudio = false;
        video.play().catch(() => {});
      });
    };

    video.addEventListener('volumechange', () => {
      if (syncingAudio || video.readyState === 0) return;
      userMuted = video.muted;
      if (!video.muted && video.volume > 0) volume = video.volume;
    });

    video.addEventListener('loadedmetadata', () => {
      applyAudio();
      fitHeight();
    });
    window.addEventListener('resize', fitHeight);

    const playAt = (i) => {
      const item = playlist[i];
      if (!item) return;
      index = i;
      audioFix = 0;
      video.playsInline = true;
      video.controls = false;
      syncingAudio = true;
      if (video.getAttribute('src') !== item.src) video.src = item.src;
      syncingAudio = false;
      video.dataset.saVideoName = item.name;
      showCopy(item);
      applyAudio();
      startPlayback();
    };

    const remember = (name) => {
      if (!name || played.includes(name)) return;
      played.push(name);
      writePlayed(played);
    };

    video.addEventListener('playing', () => {
      errorStreak = 0;
      if (!userMuted && video.muted && audioFix < 2) {
        audioFix += 1;
        applyAudio();
      }
      if (!video.muted && video.volume > 0) soundUnlocked = true;
      showCopy(playlist[index]);
      remember(playlist[index]?.name);
    });

    video.addEventListener('ended', () => {
      remember(playlist[index]?.name);
      if (playlist.every((item) => played.includes(item.name))) {
        clearPlayed();
        played = [];
        playAt(0);
        return;
      }
      playAt(nextUnplayed(index + 1));
    });

    video.addEventListener('error', () => {
      errorStreak += 1;
      if (errorStreak > playlist.length) return;
      remember(playlist[index]?.name);
      if (playlist.every((item) => played.includes(item.name))) {
        clearPlayed();
        played = [];
        playAt(0);
        return;
      }
      const next = nextUnplayed(index + 1);
      if (next === index) return;
      playAt(next);
    });

    const controls = hero.querySelector('[data-sa-hero-controls]');
    const playBtn = controls?.querySelector('[data-sa-hero-play]');
    const muteBtn = controls?.querySelector('[data-sa-hero-mute]');
    const seekInput = controls?.querySelector('[data-sa-hero-seek]');
    const volumeInput = controls?.querySelector('[data-sa-hero-volume]');
    let seeking = false;

    const syncControls = () => {
      if (!controls) return;
      const paused = video.paused;
      playBtn?.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
      playBtn?.setAttribute('aria-pressed', String(!paused));
      const playIcon = playBtn?.querySelector('[data-sa-hero-play-icon]');
      const pauseIcon = playBtn?.querySelector('[data-sa-hero-pause-icon]');
      if (playIcon) playIcon.hidden = !paused;
      if (pauseIcon) pauseIcon.hidden = paused;
      const silent = video.muted || volume === 0;
      muteBtn?.setAttribute('aria-label', silent ? 'Unmute video' : 'Mute video');
      muteBtn?.setAttribute('aria-pressed', String(silent));
      const volumeOn = muteBtn?.querySelector('[data-sa-hero-volume-on]');
      const volumeOff = muteBtn?.querySelector('[data-sa-hero-volume-off]');
      if (volumeOn) volumeOn.hidden = silent;
      if (volumeOff) volumeOff.hidden = !silent;
      if (volumeInput && document.activeElement !== volumeInput) volumeInput.value = String(volume);
      if (seekInput && !seeking && video.duration) {
        seekInput.value = String(Math.round((video.currentTime / video.duration) * 1000));
      }
    };

    playBtn?.addEventListener('click', () => {
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    });
    muteBtn?.addEventListener('click', () => {
      userMuted = !video.muted;
      if (!userMuted && volume === 0) volume = defaultVolume;
      applyAudio();
      syncControls();
    });
    volumeInput?.addEventListener('input', () => {
      volume = Number(volumeInput.value);
      userMuted = volume === 0;
      applyAudio();
      syncControls();
    });
    seekInput?.addEventListener('pointerdown', () => {
      seeking = true;
    });
    const finishSeek = () => {
      seeking = false;
    };
    seekInput?.addEventListener('pointerup', finishSeek);
    seekInput?.addEventListener('change', finishSeek);
    seekInput?.addEventListener('input', () => {
      if (!video.duration) return;
      video.currentTime = (Number(seekInput.value) / 1000) * video.duration;
    });
    video.addEventListener('timeupdate', syncControls);
    video.addEventListener('play', syncControls);
    video.addEventListener('pause', syncControls);

    playAt(index);
    syncControls();
  };

  const hero = document.querySelector('[data-sa-hero]');
  if (hero) {
    const slides = [...hero.querySelectorAll('[data-sa-slide]')];
    const dotsWrap = hero.querySelector('[data-sa-dots]');
    let index = 0;
    let timer;

    const go = (next) => {
      slides[index]?.classList.remove('is-active');
      dotsWrap?.children[index]?.classList.remove('is-active');
      index = (next + slides.length) % slides.length;
      slides[index]?.classList.add('is-active');
      dotsWrap?.children[index]?.classList.add('is-active');
    };

    const videoHero = Boolean(hero.querySelector('[data-sa-hero-video]'));
    if (!videoHero) {
      slides.forEach((_, i) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', `Go to slide ${i + 1}`);
        if (i === 0) button.classList.add('is-active');
        button.addEventListener('click', () => {
          go(i);
          restart();
        });
        dotsWrap?.appendChild(button);
      });
    }

    const restart = () => {
      clearInterval(timer);
      if (!prefersReduced) timer = setInterval(() => go(index + 1), 6000);
    };
    if (!videoHero) restart();
    initHeroVideos(hero);
  }

  const bindCarousel = (trackSel, prevSel, nextSel) => {
    const track = document.querySelector(trackSel);
    if (!track) return;
    const scrollByCard = (dir) => {
      const item = track.firstElementChild;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 18;
      const width = item ? item.getBoundingClientRect().width + gap : 280;
      track.scrollBy({ left: dir * width, behavior: 'smooth' });
    };
    document.querySelector(prevSel)?.addEventListener('click', () => scrollByCard(-1));
    document.querySelector(nextSel)?.addEventListener('click', () => scrollByCard(1));
  };
  bindCarousel('[data-sa-fav-track]', '[data-sa-fav-prev]', '[data-sa-fav-next]');
  bindCarousel('[data-sa-trusted-track]', '[data-sa-trusted-prev]', '[data-sa-trusted-next]');

  document.querySelectorAll('[data-sa-blog-slider]').forEach((root) => {
    const track = root.querySelector('[data-sa-blog-track]');
    const prev = root.querySelector('[data-sa-blog-prev]');
    const next = root.querySelector('[data-sa-blog-next]');
    const dotsWrap = root.querySelector('[data-sa-blog-dots]');
    if (!track) return;

    const cards = [...track.children];
    const cardStep = () => {
      const item = track.firstElementChild;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 16;
      return item ? item.getBoundingClientRect().width + gap : 320;
    };

    const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);
    const activeIndex = () => {
      const step = cardStep();
      if (step <= 0) return 0;
      return Math.round(track.scrollLeft / step);
    };

    const updateControls = () => {
      const max = maxScroll();
      const overflow = max > 4;
      if (prev) {
        prev.hidden = !overflow;
        prev.disabled = track.scrollLeft <= 4;
      }
      if (next) {
        next.hidden = !overflow;
        next.disabled = track.scrollLeft >= max - 4;
      }
      if (dotsWrap) {
        dotsWrap.hidden = !overflow || cards.length < 2;
        const current = activeIndex();
        [...dotsWrap.children].forEach((dot, i) => {
          const on = i === current;
          dot.classList.toggle('is-active', on);
          if (on) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }
    };

    if (dotsWrap && cards.length) {
      dotsWrap.replaceChildren();
      cards.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'sa-blog-slider__dot';
        if (i === 0) dot.classList.add('is-active');
        dot.setAttribute('aria-label', `Go to story ${i + 1}`);
        dot.addEventListener('click', () => {
          track.scrollTo({ left: i * cardStep(), behavior: prefersReduced ? 'auto' : 'smooth' });
        });
        dotsWrap.appendChild(dot);
      });
    }

    prev?.addEventListener('click', () => {
      track.scrollBy({ left: -cardStep(), behavior: prefersReduced ? 'auto' : 'smooth' });
    });
    next?.addEventListener('click', () => {
      track.scrollBy({ left: cardStep(), behavior: prefersReduced ? 'auto' : 'smooth' });
    });
    track.addEventListener('scroll', updateControls, { passive: true });
    window.addEventListener('resize', updateControls);
    updateControls();
  });

  const lifestyleTrack = document.querySelector('[data-sa-lifestyle-track]');
  const lifestylePrev = document.querySelector('[data-sa-lifestyle-prev]');
  const lifestyleNext = document.querySelector('[data-sa-lifestyle-next]');
  if (lifestyleTrack && lifestylePrev && lifestyleNext) {
    const scrollLifestyle = (dir) => {
      lifestyleTrack.scrollBy({
        left: dir * lifestyleTrack.clientWidth,
        behavior: 'smooth',
      });
    };

    lifestylePrev.addEventListener('click', () => scrollLifestyle(-1));
    lifestyleNext.addEventListener('click', () => scrollLifestyle(1));
  }

  document.querySelectorAll('.sa-solutions').forEach((root) => {
    const track = root.querySelector('[data-sa-solutions-track]');
    const prev = root.querySelector('[data-sa-solutions-prev]');
    const next = root.querySelector('[data-sa-solutions-next]');
    if (!track || !prev || !next) return;

    const cardStep = () => {
      const item = track.firstElementChild;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 16;
      return item ? item.getBoundingClientRect().width + gap : 280;
    };

    const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

    const updateControls = () => {
      const max = maxScroll();
      const overflow = max > 4;
      prev.hidden = !overflow;
      next.hidden = !overflow;
      prev.disabled = track.scrollLeft <= 4;
      next.disabled = track.scrollLeft >= max - 4;
    };

    prev.addEventListener('click', () => {
      track.scrollBy({ left: -cardStep(), behavior: prefersReduced ? 'auto' : 'smooth' });
    });
    next.addEventListener('click', () => {
      track.scrollBy({ left: cardStep(), behavior: prefersReduced ? 'auto' : 'smooth' });
    });
    track.addEventListener('scroll', updateControls, { passive: true });
    window.addEventListener('resize', updateControls);
    updateControls();
  });

  const trustedTrack = document.querySelector('[data-sa-trusted-track]');
  const trustedWrap = trustedTrack?.closest('.sa-trusted__wrap');
  if (trustedTrack && trustedWrap && !prefersReduced) {
    const originals = [...trustedTrack.children];
    originals.forEach((card) => trustedTrack.appendChild(card.cloneNode(true)));

    let paused = false;
    const speed = 0.55;

    const loopWidth = () => trustedTrack.scrollWidth / 2;

    const tick = () => {
      if (!paused) {
        trustedTrack.scrollLeft += speed;
        const half = loopWidth();
        if (half > 0 && trustedTrack.scrollLeft >= half) {
          trustedTrack.scrollLeft -= half;
        }
      }
      requestAnimationFrame(tick);
    };

    trustedWrap.addEventListener('mouseenter', () => {
      paused = true;
    });
    trustedWrap.addEventListener('mouseleave', () => {
      paused = false;
    });
    trustedWrap.addEventListener('focusin', () => {
      paused = true;
    });
    trustedWrap.addEventListener('focusout', (event) => {
      if (!trustedWrap.contains(event.relatedTarget)) paused = false;
    });

    requestAnimationFrame(tick);
  }

  const reveals = document.querySelectorAll('[data-sa-reveal]');
  if (reveals.length && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.16 }
    );
    reveals.forEach((el, i) => {
      el.style.animationDelay = `${i * 60}ms`;
      io.observe(el);
    });
  } else {
    reveals.forEach((el) => el.classList.add('is-in'));
  }

  const modal = document.querySelector('[data-sa-video-modal]');
  const player = modal?.querySelector('[data-sa-video-player]');
  const defaultVideoSrc = player?.getAttribute('data-sa-video-default') || '';

  const setPlayerSource = (src) => {
    if (!player || !src) return false;
    let source = player.querySelector('source');
    if (!source) {
      source = document.createElement('source');
      source.type = 'video/mp4';
      player.appendChild(source);
    }
    if (source.getAttribute('src') !== src) {
      source.setAttribute('src', src);
    }
    player.load();
    return true;
  };

  const closeModal = () => {
    player?.pause();
    if (typeof modal.close === 'function' && modal.open) modal.close();
    document.body.style.removeProperty('overflow');
  };

  const openModal = (src) => {
    if (!modal) return;
    const nextSrc = (src || defaultVideoSrc || '').trim();
    if (nextSrc) setPlayerSource(nextSrc);
    if (typeof modal.showModal === 'function') {
      if (!modal.open) modal.showModal();
    } else {
      modal.setAttribute('open', '');
    }
    document.body.style.overflow = 'hidden';
    if (nextSrc) {
      const play = player?.play();
      if (play && typeof play.catch === 'function') play.catch(() => {});
    }
  };

  document.querySelectorAll('[data-sa-video-open]').forEach((btn) => {
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      openModal(btn.getAttribute('data-sa-video-src'));
    });
  });
  modal?.querySelector('[data-sa-video-close]')?.addEventListener('click', closeModal);
  modal?.addEventListener('click', (event) => {
    if (event.target === modal) closeModal();
  });
  modal?.addEventListener('close', () => {
    player?.pause();
    document.body.style.removeProperty('overflow');
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modal?.open) closeModal();
  });
})();
